import React from "react";
import { RouteObject, useRouteError } from "react-router-dom";
import ExcalidrawApp from "./App";
import ShareRoute from "./components/share-view/ShareRoute";
import MyFilesPage from "./components/my-files/MyFilesPage";
import OAuthConsentPage from "./components/oauth/OAuthConsentPage";
import AgentsPage from "./components/agents/AgentsPage";
import CloudFile404 from "./components/CloudFile404";

/**
 * The app's route table, extracted from `index.tsx` (which has mount side
 * effects) so it is unit-testable with a memory router.
 *
 * Unknown paths render the branded `CloudFile404` (which hints that shared
 * mindmaps look like `/share/…`) via the `*` splat, and every route carries an
 * `errorElement` so a render/loader error shows the same branded surface
 * instead of react-router's unstyled default error page.
 */

/** Thin wrapper: log the router error for diagnostics, render the branded 404. */
export const RouteError: React.FC = () => {
  const error = useRouteError();
  // eslint-disable-next-line no-console
  console.error(error);
  return <CloudFile404 />;
};

export const appRoutes: RouteObject[] = [
  {
    path: "/",
    element: <ExcalidrawApp />,
    errorElement: <RouteError />,
  },
  {
    // Per-file cloud editor. Same app shell; the fileId is read from the path.
    // React Router ranks the static `/files` list route above this dynamic
    // segment, so `/files` still renders the list and `/files/<id>` the editor.
    path: "/files/:fileId",
    element: <ExcalidrawApp />,
    errorElement: <RouteError />,
  },
  {
    // Legacy editor path, kept so `/f/<id>` links shared/bookmarked before the
    // rename still open the editor instead of hitting the branded 404.
    path: "/f/:fileId",
    element: <ExcalidrawApp />,
    errorElement: <RouteError />,
  },
  {
    // Canonical read-only share surface. Standalone (NOT the editor shell) so it
    // never mounts autosave/buffer writes — read-only by construction. Renders
    // chrome-less inside an iframe (embed) and with light chrome otherwise.
    path: "/share/:fileId",
    element: <ShareRoute />,
    errorElement: <RouteError />,
  },
  {
    // My Files: browse/rename/duplicate/delete saved mindmaps.
    path: "/files",
    element: <MyFilesPage />,
    errorElement: <RouteError />,
  },
  {
    // Consent page for agent logins; the oauth catch-all serves its shell.
    path: "/oauth/consent",
    element: <OAuthConsentPage />,
    errorElement: <RouteError />,
  },
  {
    // Agents: connect an agent, list and disconnect connected ones.
    path: "/agents",
    element: <AgentsPage />,
    errorElement: <RouteError />,
  },
  {
    // Catch-all: any unknown path gets the uniform branded 404.
    path: "*",
    element: <CloudFile404 />,
    errorElement: <RouteError />,
  },
];
